import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { ICVCategoryDistribution } from "../../../types/contractDashboard";
import styles from "./icvScoreDistribution.module.scss";

interface ICVScoreDistributionProps {
  data: ICVCategoryDistribution[];
}

const ICVScoreDistribution: React.FC<ICVScoreDistributionProps> = ({
  data: rawData,
}) => {
  const { chartData, highPercentage } = useMemo(() => {
    const total = rawData.reduce((sum, item) => sum + item.contract_count, 0);

    const categoryMap: Record<string, { label: string; color: string }> = {
      High: { label: "ICV Score >80%", color: "#08ADC2" },
      Medium: { label: "ICV Score 50-80%", color: "#f59e0b" },
      Low: { label: "ICV Score <50%", color: "#ef4444" },
    };

    const processedData = rawData.map((item) => ({
      name: categoryMap[item.icv_category]?.label || item.icv_category,
      value: total > 0 ? Math.round((item.contract_count / total) * 100) : 0,
      count: item.contract_count,
      color: categoryMap[item.icv_category]?.color || "#888",
    }));

    const highItem = rawData.find((item) => item.icv_category === "High");
    const highPct =
      highItem && total > 0
        ? Math.round((highItem.contract_count / total) * 100)
        : 0;

    return { chartData: processedData, highPercentage: highPct };
  }, [rawData]);

  const CustomTooltip = ({ active, payload, coordinate }: any) => {
    if (active && payload && payload.length && coordinate) {
      // Calculate if tooltip should appear on right or left side
      const chartCenterX = coordinate.chartX || 0;
      const mouseX = coordinate.x || 0;
      const isRightSide = mouseX > chartCenterX;

      return (
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "10px 14px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            position: "relative",
            maxWidth: "220px",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "#111827",
              fontWeight: 600,
              mb: 0.5,
              fontSize: "13px",
            }}
          >
            {payload[0].name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: payload[0].payload.color,
              fontWeight: 500,
              fontSize: "13px",
            }}
          >
            {payload[0].value}% ({payload[0].payload.count} contracts)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.titleSection}>
        <Typography variant="h6" className={styles.title}>
          ICV Score Distribution
        </Typography>
        <Typography variant="body2" className={styles.subtitle}>
          Contracts with ICV Score breakdown.
        </Typography>
      </Box>

      <Box
        className={styles.chartContainer}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "0",
        }}
      >
        {/* Donut Chart */}
        <Box sx={{ position: "relative", width: "100%", height: "280px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={80}
                outerRadius={125}
                paddingAngle={3}
                dataKey="value"
                strokeWidth={0}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
                allowEscapeViewBox={{ x: true, y: true }}
                offset={40}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Center Text */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "#111827",
                fontWeight: 700,
                lineHeight: 1,
                fontSize: "48px",
              }}
            >
              {highPercentage}%
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
                fontSize: "14px",
                mt: 1.5,
                fontWeight: 500,
              }}
            >
              High ICV Score
            </Typography>
          </Box>
        </Box>

        {/* Legend */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 3,
            mt: 2,
            width: "100%",
          }}
        >
          {chartData.map((item, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Box
                sx={{
                  width: "12px",
                  height: "12px",
                  borderRadius: "3px",
                  backgroundColor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: "#374151", fontSize: "13px", fontWeight: 500 }}
              >
                {item.name}{" "}
                <Box
                  component="span"
                  sx={{ color: "#6b7280", fontWeight: 400 }}
                >
                  ({item.value}%)
                </Box>
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ICVScoreDistribution;
